import React, { useState, useEffect, useCallback } from 'react';
import { getFeeConfig, updateFeeConfig } from '../services/settingsService';
import LoadingSpinner from './common/LoadingSpinner';

/**
 * SettingsView — admin settings page.
 *
 * Currently exposes a single setting: the platform fee composition. The fee
 * charged on top of every pledge principal is a fixed amount plus a
 * percentage of the principal:
 *
 *     platform_fee = fixed_fee + (percentage × principal)
 *
 * The user is charged `principal + platform_fee`; Stripe's processing cost is
 * absorbed by the platform out of this fee. Saving a new composition applies
 * to every NEW pledge immediately — existing pledges keep the fee snapshot
 * stored when they were created.
 *
 * The form works in euros / percent for readability and converts to the
 * backend's integer cents / basis points on save.
 */
const SettingsView = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Form fields are strings so the inputs stay controlled while editing.
  const [fixedFeeEuros, setFixedFeeEuros] = useState('');
  const [percent, setPercent] = useState('');
  const [meta, setMeta] = useState(null); // { updated_at, updated_by, source }

  const applyConfig = useCallback((config) => {
    const fixedCents = Number(config.fixed_fee_cents) || 0;
    const bps = Number(config.percent_bps) || 0;
    setFixedFeeEuros((fixedCents / 100).toFixed(2));
    // percent_bps / 100 gives percent (300 → 3); trim trailing zeros.
    setPercent(String(bps / 100));
    setMeta({
      updated_at: config.updated_at,
      updated_by: config.updated_by,
      source: config.source,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const config = await getFeeConfig();
        if (!cancelled) applyConfig(config);
      } catch (err) {
        console.error('Error loading fee config:', err);
        if (!cancelled) setError('Failed to load fee settings. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [applyConfig]);

  // Parse + validate the form. Returns { fixedFeeCents, percentBps } or throws
  // an Error with a user-facing message.
  const parseAndValidate = () => {
    const euros = parseFloat(fixedFeeEuros);
    const pct = parseFloat(percent);

    if (Number.isNaN(euros) || euros < 0) {
      throw new Error('Fixed fee must be a non-negative amount.');
    }
    if (Number.isNaN(pct) || pct < 0) {
      throw new Error('Percentage must be a non-negative number.');
    }
    if (pct > 100) {
      throw new Error('Percentage cannot exceed 100%.');
    }

    // euros → cents and percent → basis points, both as integers.
    const fixedFeeCents = Math.round(euros * 100);
    const percentBps = Math.round(pct * 100);
    return { fixedFeeCents, percentBps };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage('');

    let parsed;
    try {
      parsed = parseAndValidate();
    } catch (validationErr) {
      setError(validationErr.message);
      return;
    }

    setSaving(true);
    try {
      const updated = await updateFeeConfig(parsed.fixedFeeCents, parsed.percentBps);
      applyConfig(updated);
      setSuccessMessage('Fee settings saved. New pledges will use the updated fee.');
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      console.error('Error saving fee config:', err);
      setError(err.message || 'Failed to save fee settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Live preview of the total a sample €50 pledge would cost.
  const renderPreview = () => {
    const euros = parseFloat(fixedFeeEuros);
    const pct = parseFloat(percent);
    if (Number.isNaN(euros) || Number.isNaN(pct)) return null;

    const sampleEuros = 50;
    // Mirror the backend's integer-cent ceil so the preview matches the charge.
    const sampleCents = sampleEuros * 100;
    const percentBps = Math.round(pct * 100);
    const fixedCents = Math.round(euros * 100);
    const feeCents = fixedCents + Math.ceil((sampleCents * percentBps) / 10000);
    const totalCents = sampleCents + feeCents;
    return (
      <p className="text-sm text-gray-600 mt-2">
        Example: a €{sampleEuros.toFixed(2)} pledge would be charged a fee of{' '}
        <span className="font-semibold">€{(feeCents / 100).toFixed(2)}</span> for a total of{' '}
        <span className="font-semibold">€{(totalCents / 100).toFixed(2)}</span>.
      </p>
    );
  };

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Settings</h1>

      {successMessage && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white shadow-lg rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-1">Platform Fee</h2>
        <p className="text-sm text-gray-500 mb-6">
          The fee charged on top of each pledge: a fixed amount plus a percentage of the
          pledge. The platform absorbs Stripe&apos;s processing cost out of this fee. Changes
          apply to new pledges only.
        </p>

        {loading ? (
          <div className="py-8 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <form onSubmit={handleSave}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="fixedFee" className="block text-sm font-medium text-gray-700 mb-1">
                  Fixed fee (€)
                </label>
                <input
                  id="fixedFee"
                  type="number"
                  min="0"
                  step="0.01"
                  value={fixedFeeEuros}
                  onChange={(e) => setFixedFeeEuros(e.target.value)}
                  disabled={saving}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none disabled:bg-gray-100"
                  placeholder="1.00"
                />
                <p className="text-xs text-gray-500 mt-1">Flat amount added to every pledge.</p>
              </div>

              <div>
                <label htmlFor="percent" className="block text-sm font-medium text-gray-700 mb-1">
                  Percentage (%)
                </label>
                <input
                  id="percent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  disabled={saving}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none disabled:bg-gray-100"
                  placeholder="3"
                />
                <p className="text-xs text-gray-500 mt-1">Percentage of the pledge principal.</p>
              </div>
            </div>

            {renderPreview()}

            {meta && (
              <p className="text-xs text-gray-400 mt-4">
                {meta.source === 'db'
                  ? `Last updated ${meta.updated_at ? new Date(meta.updated_at).toLocaleString() : ''}${meta.updated_by ? ` by ${meta.updated_by}` : ''}.`
                  : 'Currently using the default fee (no custom value saved yet).'}
              </p>
            )}

            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  saving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                } transition-colors duration-200`}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SettingsView;
